<template>
  <el-dialog
    v-model="visible"
    class="ai-assistant-dialog"
    width="min(92vw, 1440px)"
    :show-close="false"
    :close-on-click-modal="false"
    :z-index="ASSISTANT_DIALOG_Z_INDEX"
    append-to-body
    :destroy-on-close="false"
    @opened="handleOpened"
  >
    <template #header>
      <div class="assistant-header">
        <div class="assistant-brand">
          <div class="assistant-logo" aria-hidden="true">Z</div>
          <div>
            <h2>客户自助助手</h2>
            <p><span></span>智能客服 · 找货/商品识别/订单/物流/发票/合同</p>
          </div>
        </div>

        <div class="assistant-header-actions">
          <span class="assistant-state is-primary">AI对话</span>
          <span class="assistant-state" :class="{ 'is-warning': assistantUnavailable || busy }">
            {{ statusText }}
          </span>
          <button
            type="button"
            class="assistant-header-button"
            data-testid="assistant-new-conversation"
            :disabled="busy"
            @click="startNewConversation"
          >
            <i class="fas fa-rotate"></i>
            <span>新对话</span>
          </button>
          <button type="button" class="assistant-close" aria-label="关闭 AI 助手" @click="closeDrawer">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </template>

    <div class="assistant-shell">
      <section ref="chatThreadRef" class="assistant-chat-thread" data-testid="assistant-chat-thread">
        <article
          v-for="message in chatMessages"
          :key="message.id"
          class="assistant-message"
          :class="message.role === 'user' ? 'is-user' : 'is-assistant'"
          :data-testid="message.role === 'user' ? 'assistant-message-user' : 'assistant-message-assistant'"
        >
          <div v-if="message.role !== 'user'" class="assistant-message-icon">
            <i class="far fa-comment-dots"></i>
          </div>

          <div class="assistant-message-bubble">
            <div v-if="message.role !== 'user'" class="assistant-message-meta">
              <strong>{{ serviceIntentLabel(message.intentCode || serviceIntent) }}</strong>
            </div>

            <p class="assistant-message-text">{{ message.text }}</p>
            <p v-if="message.attachmentName" class="assistant-message-attachment">
              <i class="fas fa-paperclip"></i>{{ message.attachmentName }}
            </p>

            <div v-if="message.businessRows?.length" class="assistant-business-list">
              <div
                v-for="(row, rowIndex) in message.businessRows"
                :key="`${message.id}-business-${rowIndex}`"
                class="assistant-business-card"
              >
                <div class="assistant-business-grid">
                  <p v-for="field in businessFieldsForRow(row)" :key="field.key">
                    <span>{{ field.label }}：</span>
                    <a
                      v-if="isExternalUrl(field.value)"
                      :href="field.value"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ field.key === 'invoiceUrl' ? '下载发票' : field.key === 'contractUrl' ? '下载合同' : '打开' }}
                    </a>
                    <strong v-else>{{ formatBusinessValue(field) }}</strong>
                  </p>
                </div>
                <button v-if="row.canPay === true" type="button" class="assistant-pay-button" @click="openOrderCenter(row)">
                  去订单中心支付
                </button>
              </div>
            </div>

            <div v-if="message.suggestions?.length" class="assistant-suggestions">
              <button
                v-for="suggestion in message.suggestions"
                :key="`${message.id}-${suggestion.label}`"
                type="button"
                :disabled="busy"
                @click="sendMessage(suggestion.prompt || suggestion.label)"
              >
                {{ suggestion.label }}
              </button>
            </div>
          </div>
        </article>

        <article v-if="busy" class="assistant-message is-assistant" data-testid="assistant-message-assistant">
          <div class="assistant-message-icon"><i class="far fa-comment-dots"></i></div>
          <div class="assistant-message-bubble assistant-loading">
            <span></span><span></span><span></span>
            正在查询
          </div>
        </article>
      </section>

      <section
        class="assistant-composer"
        :class="{ 'is-dragging': isAttachmentDragging }"
        @dragenter.prevent="handleAttachmentDragEnter"
        @dragover.prevent="handleAttachmentDragOver"
        @dragleave.prevent="handleAttachmentDragLeave"
        @drop.prevent="handleAttachmentDrop"
      >
        <div v-if="isAttachmentDragging" class="assistant-drop-overlay" aria-hidden="true">
          <i class="fas fa-cloud-arrow-up"></i>
          <strong>松开即可添加附件</strong>
          <span>支持图片、PDF、Word 与 Excel</span>
        </div>
        <div class="assistant-input-row">
          <textarea
            v-model="chatInput"
            data-testid="assistant-chat-input"
            :disabled="busy"
            placeholder="输入订单号、最近一单、近7天订单、发票状态、物流信息、支付状态、合同下载、CAS 或货号..."
            @keydown.enter.exact.prevent="sendMessage()"
          ></textarea>
          <button
            type="button"
            class="assistant-send"
            data-testid="assistant-send-button"
            :disabled="busy || (!chatInput.trim() && !attachmentFile)"
            aria-label="发送"
            @click="sendMessage()"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>

        <div class="assistant-attachment-row">
          <div class="assistant-attachment-actions">
            <button type="button" :disabled="busy" @click="imageInputRef?.click()">
              <i class="far fa-image"></i> 图片
            </button>
            <button type="button" :disabled="busy" @click="fileInputRef?.click()">
              <i class="far fa-file-lines"></i> 文件
            </button>
            <input ref="imageInputRef" type="file" accept="image/*" hidden @change="handleFileChange" />
            <input ref="fileInputRef" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" hidden @change="handleFileChange" />
          </div>

          <div v-if="attachmentFile" class="assistant-attachment-chip">
            <i class="fas fa-paperclip"></i>
            <span>{{ attachmentFile.name }}</span>
            <button type="button" aria-label="移除附件" :disabled="busy" @click="clearAttachment">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <span v-else class="assistant-no-attachment">未携带附件</span>
        </div>
      </section>

      <section v-if="resultItems.length" class="assistant-results" data-testid="assistant-inquiry-results">
        <div class="assistant-results-title">
          <div>
            <h3>AI 解析结果</h3>
          </div>
          <span>{{ matchedItems.length }} / {{ resultItems.length }} 已匹配</span>
        </div>

        <div
          v-if="cartFeedback"
          class="assistant-cart-feedback"
          :class="`is-${cartFeedback.type}`"
          :role="cartFeedback.type === 'error' ? 'alert' : 'status'"
          :aria-live="cartFeedback.type === 'error' ? 'assertive' : 'polite'"
          data-testid="assistant-cart-feedback"
        >
          <span class="assistant-cart-feedback-icon" aria-hidden="true">
            <i :class="cartFeedbackIcon"></i>
          </span>
          <div class="assistant-cart-feedback-copy">
            <strong>{{ cartFeedback.title }}</strong>
            <span v-if="cartFeedback.detail">{{ cartFeedback.detail }}</span>
          </div>
          <button
            v-if="cartFeedback.type === 'success' || cartFeedback.type === 'warning'"
            type="button"
            class="assistant-cart-feedback-action"
            @click="goToCart"
          >
            查看购物车
          </button>
          <button
            type="button"
            class="assistant-cart-feedback-close"
            aria-label="关闭加购提示"
            @click="cartFeedback = null"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="hasOrderInfo" class="assistant-order-info">
          <p v-if="orderInfo.customerName"><span>单位：</span>{{ orderInfo.customerName }}</p>
          <p v-if="orderInfo.contactPerson"><span>联系人：</span>{{ orderInfo.contactPerson }}</p>
          <p v-if="orderInfo.contactPhone"><span>电话：</span>{{ orderInfo.contactPhone }}</p>
          <p v-if="orderInfo.contactEmail"><span>邮箱：</span>{{ orderInfo.contactEmail }}</p>
          <p v-if="orderInfo.address"><span>地址：</span>{{ orderInfo.address }}</p>
        </div>

        <div class="assistant-result-grid">
          <article
            v-for="item in resultItems"
            :key="item.key"
            class="assistant-result-card"
            :class="[
              item.matched ? 'is-matched' : 'is-unmatched',
              { 'is-added': wasItemAdded(item) },
            ]"
          >
            <div class="assistant-result-main">
              <div>
                <div class="assistant-result-badges">
                  <span>{{ item.matched ? '已匹配' : '待确认' }}</span>
                  <span v-if="item.dangerous" class="is-danger">危化品</span>
                  <span v-if="wasItemAdded(item)" class="is-added"><i class="fas fa-check"></i> 已加购</span>
                </div>
                <h4>{{ item.productName }}</h4>
                <p v-if="item.cas">CAS：{{ item.cas }}</p>
                <p v-if="item.skuCode">货号：{{ item.skuCode }}</p>
                <p v-if="item.brand">品牌：{{ item.brand }}</p>
                <p v-if="item.spec">规格：{{ item.spec }}</p>
                <p v-if="item.reason" class="assistant-result-reason">{{ item.reason }}</p>
              </div>
              <strong v-if="item.salePrice !== null && item.salePrice !== undefined" class="assistant-result-price">
                ¥{{ formatMoney(item.salePrice) }}
              </strong>
            </div>
            <div class="assistant-result-actions">
              <el-input-number v-model="item.quantity" :min="1" :precision="0" controls-position="right" size="small" />
              <button
                type="button"
                :disabled="!cartPayloadFromResultItem(item) || isItemAdding(item) || addingAll"
                @click="addMatchedItem(item)"
              >
                <i :class="wasItemAdded(item) ? 'fas fa-check' : 'fas fa-cart-plus'"></i>
                {{ itemCartActionLabel(item) }}
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="assistant-footer">
        <span>支持图片、PDF、Word 与 Excel，单个附件不超过 10MB</span>
        <div>
          <button type="button" class="assistant-cancel-button" @click="closeDrawer">取消</button>
          <button
            type="button"
            class="assistant-apply-button"
            :disabled="!matchedItems.length || addingAll || busy"
            @click="addAllMatched"
          >
            {{ addingAll ? '正在加入购物车...' : '全部加入购物车' }}
          </button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { ElMessage } from 'element-plus'
import { assistantApi } from '@/apis/assistant'
import { useCartStore } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import {
  buildAssistantMessage,
  businessFieldsForRow,
  cartPayloadFromResultItem,
  createChatMessage,
  createWelcomeMessages,
  isExternalUrl,
  normalizeInquiryResult,
  serviceIntentLabel,
  summarizeInquiryResult,
} from '@/utils/assistant.mjs'

const emit = defineEmits(['login-required'])
const router = useRouter()
const cartStore = useCartStore()
const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)

const CONVERSATION_STORAGE_KEY = 'mall_ai_assistant_conversation_no'
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024
const ASSISTANT_DIALOG_Z_INDEX = 4000
const ASSISTANT_MESSAGE_Z_INDEX = ASSISTANT_DIALOG_Z_INDEX + 10
const DOCUMENT_ATTACHMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx'])
const IMAGE_ATTACHMENT_EXTENSIONS = new Set([
  'avif', 'bmp', 'gif', 'heic', 'heif', 'ico', 'jpeg', 'jpg', 'png', 'svg', 'tif', 'tiff', 'webp',
])

const visible = ref(false)
const chatThreadRef = ref(null)
const imageInputRef = ref(null)
const fileInputRef = ref(null)
const chatInput = ref('')
const chatMessages = ref(createWelcomeMessages())
const serviceIntent = ref('GENERAL')
const conversationNo = ref(readStoredConversationNo())
const attachmentFile = ref(null)
const isAttachmentDragging = ref(false)
const assistantStatus = ref({ checked: false, modelConfigured: null, modelName: '', message: '' })
const busy = ref(false)
const resultItems = ref([])
const orderInfo = ref({})
const addingItemKeys = ref(new Set())
const addedItemKeys = ref(new Set())
const addingAll = ref(false)
const cartFeedback = ref(null)
let attachmentDragDepth = 0

const isLoggedIn = computed(() => Boolean(userStore.token || userInfo.value?.token || userInfo.value?.id || userInfo.value?.userName))
const assistantUnavailable = computed(() => assistantStatus.value.checked && assistantStatus.value.modelConfigured === false)
const statusText = computed(() => {
  if (busy.value) return '正在回复'
  if (assistantUnavailable.value) return 'AI待配置'
  return serviceIntent.value === 'GENERAL' ? '会话中' : serviceIntentLabel(serviceIntent.value)
})
const matchedItems = computed(() => resultItems.value.filter((item) => cartPayloadFromResultItem(item)))
const hasOrderInfo = computed(() => Object.values(orderInfo.value || {}).some(Boolean))
const cartFeedbackIcon = computed(() => ({
  success: 'fas fa-check',
  warning: 'fas fa-exclamation',
  error: 'fas fa-times',
}[cartFeedback.value?.type] || 'fas fa-info'))

function showAssistantMessage(type, message) {
  ElMessage({
    type,
    message,
    zIndex: ASSISTANT_MESSAGE_Z_INDEX,
    customClass: 'ai-assistant-message',
    duration: type === 'error' ? 4500 : 3000,
    showClose: type === 'error',
  })
}

function setCartFeedback(type, title, detail = '') {
  cartFeedback.value = { type, title, detail }
}

function resolveErrorMessage(error, fallback = '') {
  if (typeof error === 'string') return error
  const responseMessage = error?.response?.data?.message
  if (typeof responseMessage === 'string' && responseMessage.trim()) return responseMessage.trim()
  return error?.message || fallback
}

function readStoredConversationNo() {
  try {
    return window.sessionStorage.getItem(CONVERSATION_STORAGE_KEY) || ''
  } catch (error) {
    return ''
  }
}

function rememberConversationNo(nextConversationNo) {
  if (!nextConversationNo) return
  conversationNo.value = nextConversationNo
  try {
    window.sessionStorage.setItem(CONVERSATION_STORAGE_KEY, nextConversationNo)
  } catch (error) {
    // sessionStorage 在隐私模式下可能不可用，不影响当前对话。
  }
}

function clearStoredConversationNo() {
  conversationNo.value = ''
  try {
    window.sessionStorage.removeItem(CONVERSATION_STORAGE_KEY)
  } catch (error) {
    // 忽略浏览器存储限制。
  }
}

function openDrawer() {
  visible.value = true
}

function closeDrawer() {
  resetAttachmentDragState()
  visible.value = false
}

async function handleOpened() {
  await Promise.all([fetchAssistantStatus(), scrollChatToBottom()])
}

async function fetchAssistantStatus() {
  try {
    const status = await assistantApi.fetchStatus()
    assistantStatus.value = {
      checked: true,
      modelConfigured: status?.modelConfigured === true,
      modelName: status?.modelName || '',
      message: status?.message || '',
    }
  } catch (error) {
    assistantStatus.value = { checked: false, modelConfigured: null, modelName: '', message: '' }
  }
}

function startNewConversation() {
  if (busy.value) return
  clearStoredConversationNo()
  chatMessages.value = createWelcomeMessages()
  chatInput.value = ''
  serviceIntent.value = 'GENERAL'
  resultItems.value = []
  orderInfo.value = {}
  addedItemKeys.value = new Set()
  cartFeedback.value = null
  clearAttachment()
  showAssistantMessage('success', '已开始新对话')
  scrollChatToBottom()
}

function attachmentExtension(file) {
  return String(file?.name || '').split('.').pop()?.toLowerCase() || ''
}

function isSupportedAttachment(file) {
  const extension = attachmentExtension(file)
  return file?.type?.startsWith('image/')
    || IMAGE_ATTACHMENT_EXTENSIONS.has(extension)
    || DOCUMENT_ATTACHMENT_EXTENSIONS.has(extension)
}

function selectAttachment(file) {
  if (!file) return
  if (!isSupportedAttachment(file)) {
    showAssistantMessage('warning', '仅支持图片、PDF、Word 与 Excel 文件')
    return
  }
  if (file.size > MAX_ATTACHMENT_SIZE) {
    showAssistantMessage('warning', '单个附件不能超过 10MB')
    return
  }
  attachmentFile.value = file
}

function handleFileChange(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  selectAttachment(file)
}

function dragContainsFiles(event) {
  return Array.from(event.dataTransfer?.types || []).includes('Files')
}

function resetAttachmentDragState() {
  attachmentDragDepth = 0
  isAttachmentDragging.value = false
}

function handleAttachmentDragEnter(event) {
  if (busy.value || !dragContainsFiles(event)) return
  attachmentDragDepth += 1
  isAttachmentDragging.value = true
}

function handleAttachmentDragOver(event) {
  if (busy.value || !dragContainsFiles(event)) return
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
  isAttachmentDragging.value = true
}

function handleAttachmentDragLeave() {
  if (!isAttachmentDragging.value) return
  attachmentDragDepth = Math.max(0, attachmentDragDepth - 1)
  if (attachmentDragDepth === 0) isAttachmentDragging.value = false
}

function handleAttachmentDrop(event) {
  resetAttachmentDragState()
  if (busy.value) return
  const files = Array.from(event.dataTransfer?.files || [])
  if (!files.length) return
  if (files.length > 1) showAssistantMessage('info', '一次只能添加一个附件，已选择第一个文件')
  selectAttachment(files[0])
}

function clearAttachment() {
  attachmentFile.value = null
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('附件读取失败'))
    reader.readAsDataURL(file)
  })
}

function applyInquiryResponse(data = {}) {
  const normalized = normalizeInquiryResult(data)
  resultItems.value = normalized.items
  orderInfo.value = normalized.orderInfo
  addedItemKeys.value = new Set()
  cartFeedback.value = null
  rememberConversationNo(data.conversationNo)
}

async function parseAttachment(text) {
  const file = attachmentFile.value
  if (!file) return null
  if (file.type?.startsWith('image/')) {
    return assistantApi.parseOrder({
      text,
      images: [await fileToDataUrl(file)],
    })
  }
  const formData = new FormData()
  formData.append('text', text || '')
  formData.append('files', file, file.name)
  return assistantApi.parseOrderWithFiles(formData)
}

async function sendMessage(rawText = chatInput.value) {
  if (busy.value) return
  const prompt = String(rawText || '').trim()
  const selectedAttachment = attachmentFile.value
  if (!prompt && !selectedAttachment) {
    showAssistantMessage('warning', '请输入要咨询的问题，或上传图片/文件')
    return
  }

  const displayText = prompt || `请识别附件：${selectedAttachment.name}`
  const intentCode = selectedAttachment ? 'INQUIRY' : 'AUTO'
  chatMessages.value.push(createChatMessage('user', {
    intentCode,
    text: displayText,
    attachmentName: selectedAttachment?.name || '',
  }))
  chatInput.value = ''
  serviceIntent.value = intentCode
  busy.value = true
  await scrollChatToBottom()

  try {
    if (selectedAttachment) {
      const data = await parseAttachment(displayText)
      applyInquiryResponse(data || {})
      chatMessages.value.push(createChatMessage('assistant', {
        intentCode: 'INQUIRY',
        text: summarizeInquiryResult(data || {}),
        suggestions: [
          { label: '补充规格数量', prompt: `${displayText}\n补充规格：` },
        ],
      }))
      clearAttachment()
    } else {
      const data = await assistantApi.chat({
        intentCode,
        conversationNo: conversationNo.value || undefined,
        text: displayText,
        sourceChannel: 'PULUOHUI_MALL_WEB',
      })
      rememberConversationNo(data?.conversationNo)
      const resolvedIntent = data?.intentCode || intentCode
      serviceIntent.value = resolvedIntent
      if (resolvedIntent === 'INQUIRY'
        && (Array.isArray(data?.items) || Array.isArray(data?.matched) || Array.isArray(data?.unmatched))) {
        applyInquiryResponse(data)
      }
      chatMessages.value.push(buildAssistantMessage(data || {}, resolvedIntent))
    }
  } catch (error) {
    const message = resolveErrorMessage(error)
    chatMessages.value.push(createChatMessage('assistant', {
      intentCode,
      text: message || '这次没有查到稳定结果。您可以换完整订单号、按时间范围或补充商品规格后再试。',
    }))
    if (!error?.isAuthError) showAssistantMessage('error', message || 'AI 暂时没有查到结果')
  } finally {
    busy.value = false
    await scrollChatToBottom()
  }
}

function ensureLoggedIn() {
  if (isLoggedIn.value) return true
  setCartFeedback('warning', '登录后才能加入购物车', '登录成功后可继续保留当前商品和数量。')
  showAssistantMessage('warning', '请先登录后再加入购物车')
  emit('login-required')
  return false
}

function itemLoadingKey(item) {
  return String(item.key || item.skuId || '')
}

function isItemAdding(item) {
  return addingItemKeys.value.has(itemLoadingKey(item))
}

function wasItemAdded(item) {
  return addedItemKeys.value.has(itemLoadingKey(item))
}

function itemCartActionLabel(item) {
  if (isItemAdding(item)) return '正在加入...'
  if (!cartPayloadFromResultItem(item)) return '暂不可加入'
  if (wasItemAdded(item)) return '继续加入'
  return '加入购物车'
}

async function addMatchedItem(item, { silent = false, refresh = true } = {}) {
  const payload = cartPayloadFromResultItem(item)
  if (!payload) return false
  if (!ensureLoggedIn()) return false
  const key = itemLoadingKey(item)
  addingItemKeys.value = new Set([...addingItemKeys.value, key])
  try {
    await cartStore.addCartItemAction(payload)
    if (refresh) await cartStore.getCartList()
    addedItemKeys.value = new Set([...addedItemKeys.value, key])
    if (!silent) {
      const productName = item.productName || item.skuCode || '该商品'
      const quantity = Number(item.quantity || payload.count || 1)
      setCartFeedback(
        'success',
        `${productName}已加入购物车`,
        `本次加入 ${quantity} 件，购物车数量已更新。`,
      )
      showAssistantMessage('success', '已加入购物车')
    }
    return true
  } catch (error) {
    const message = resolveErrorMessage(error)
    if (!error?.isAuthError && !silent) {
      const errorMessage = message || '添加购物车失败'
      setCartFeedback('error', '加入购物车失败', `${errorMessage}，请稍后重试。`)
      showAssistantMessage('error', errorMessage)
    }
    return false
  } finally {
    const nextKeys = new Set(addingItemKeys.value)
    nextKeys.delete(key)
    addingItemKeys.value = nextKeys
  }
}

async function addAllMatched() {
  if (!matchedItems.value.length || addingAll.value || !ensureLoggedIn()) return
  addingAll.value = true
  let addedCount = 0
  try {
    for (const item of matchedItems.value) {
      if (await addMatchedItem(item, { silent: true, refresh: false })) addedCount += 1
    }
    if (addedCount) {
      await cartStore.getCartList()
      const failedCount = matchedItems.value.length - addedCount
      if (failedCount) {
        setCartFeedback(
          'warning',
          `已加入 ${addedCount} 种商品，${failedCount} 种未加入`,
          '购物车数量已更新，未加入的商品可稍后单独重试。',
        )
        showAssistantMessage('warning', `已加入 ${addedCount} 种商品，${failedCount} 种未加入`)
      } else {
        setCartFeedback('success', `已将 ${addedCount} 种商品全部加入购物车`, '购物车数量已更新，可前往购物车确认。')
        showAssistantMessage('success', `已加入 ${addedCount} 种商品到购物车`)
        closeDrawer()
      }
    } else {
      setCartFeedback('error', '商品未能加入购物车', '请检查商品状态或网络连接后重试。')
      showAssistantMessage('error', '没有商品成功加入购物车')
    }
  } finally {
    addingAll.value = false
  }
}

function goToCart() {
  visible.value = false
  router.push('/cart/cart')
}

function openOrderCenter(row) {
  if (!ensureLoggedIn()) return
  visible.value = false
  router.push({ path: '/order/all', query: row.orderNo ? { saleOrderNo: row.orderNo } : undefined })
}

function formatMoney(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number.toFixed(2) : '--'
}

function formatBusinessValue(field) {
  if (['totalAmount', 'receivedAmount', 'unpaidAmount', 'amount'].includes(field.key)) {
    return `¥${formatMoney(field.value)}`
  }
  return String(field.value)
}

async function scrollChatToBottom() {
  await nextTick()
  const node = chatThreadRef.value
  if (node) node.scrollTop = node.scrollHeight
}

defineExpose({
  openDrawer,
  closeDrawer,
})
</script>

<style>
.ai-assistant-dialog.el-dialog {
  display: flex;
  height: min(88dvh, 860px);
  min-height: min(640px, calc(100dvh - 24px));
  flex-direction: column;
  overflow: hidden;
  margin: max(12px, 4dvh) auto !important;
  border-radius: 22px;
  background: #f7f9fc;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.24);
}

.ai-assistant-dialog .el-dialog__header {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
  background: #ffffff;
}

.ai-assistant-dialog .el-dialog__body {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0;
}

.ai-assistant-dialog .el-dialog__footer {
  flex: 0 0 auto;
  padding: 0;
}

/* Element Plus 2.11.x 未应用 ElMessage 的 zIndex 参数，保留兼容层级避免消息落到弹窗遮罩下。 */
.el-message.ai-assistant-message {
  z-index: 4010 !important;
}

@media (max-width: 768px) {
  .ai-assistant-dialog.el-dialog {
    width: calc(100vw - 20px) !important;
    height: calc(100dvh - 20px);
    min-height: 0;
    margin: 10px auto !important;
    border-radius: 16px;
  }
}
</style>

<style scoped>
.assistant-header {
  display: flex;
  min-height: 84px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 24px;
  border-bottom: 1px solid #edf1f5;
}

.assistant-brand,
.assistant-header-actions,
.assistant-message-meta,
.assistant-suggestions,
.assistant-attachment-row,
.assistant-attachment-actions,
.assistant-results-title,
.assistant-result-actions,
.assistant-footer,
.assistant-footer > div {
  display: flex;
  align-items: center;
}

.assistant-brand { gap: 14px; }
.assistant-logo {
  display: grid;
  width: 50px;
  height: 50px;
  place-items: center;
  border-radius: 14px;
  background: linear-gradient(145deg, #063d69, #00b4d8);
  color: #fff;
  font-size: 32px;
  font-weight: 900;
  font-style: italic;
  box-shadow: 0 10px 24px rgba(0, 119, 182, 0.22);
}
.assistant-brand h2 { margin: 0; color: #10223d; font-size: 22px; font-weight: 900; }
.assistant-brand p { margin: 5px 0 0; color: #10b981; font-size: 12px; font-weight: 700; }
.assistant-brand p span { display: inline-block; width: 6px; height: 6px; margin-right: 6px; border-radius: 50%; background: #34d399; }

.assistant-header-actions { gap: 10px; }
.assistant-state { padding: 7px 12px; border-radius: 999px; background: #f3f6fa; color: #64748b; font-size: 12px; font-weight: 800; }
.assistant-state.is-primary { background: #ecfdf5; color: #059669; }
.assistant-state.is-warning { background: #fff7ed; color: #ea580c; }
.assistant-header-button,
.assistant-close { border: 0; background: transparent; cursor: pointer; }
.assistant-header-button { display: inline-flex; align-items: center; gap: 6px; padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 999px; color: #52647a; font-size: 12px; font-weight: 800; }
.assistant-header-button:hover { border-color: #7dd3fc; background: #f0f9ff; color: #0369a1; }
.assistant-header-button:disabled { cursor: not-allowed; opacity: .5; }
.assistant-close { display: grid; width: 38px; height: 38px; place-items: center; border-radius: 50%; color: #94a3b8; font-size: 18px; }
.assistant-close:hover { background: #f1f5f9; color: #334155; }

.assistant-shell {
  box-sizing: border-box;
  min-height: 100%;
  padding: 20px 22px;
}
.assistant-chat-thread {
  min-height: 300px;
  max-height: 42vh;
  overflow-y: auto;
  padding: 18px;
  border: 1px solid #eef2f6;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  scrollbar-width: thin;
}
.assistant-message { display: flex; gap: 12px; margin-bottom: 16px; }
.assistant-message:last-child { margin-bottom: 0; }
.assistant-message.is-user { justify-content: flex-end; }
.assistant-message-icon { display: grid; width: 34px; height: 34px; flex: 0 0 auto; place-items: center; border-radius: 50%; background: #eaf7ff; color: #0284c7; }
.assistant-message-bubble { max-width: min(84%, 920px); padding: 13px 16px; border: 1px solid #edf1f5; border-radius: 16px 16px 16px 5px; background: #f7f9fc; color: #475569; font-size: 14px; box-shadow: 0 3px 10px rgba(15, 23, 42, 0.035); }
.assistant-message.is-user .assistant-message-bubble { border: 0; border-radius: 16px 16px 5px 16px; background: linear-gradient(135deg, #0077b6, #0096c7); color: #fff; }
.assistant-message-meta { flex-wrap: wrap; gap: 8px; margin-bottom: 8px; color: #0369a1; font-size: 11px; }
.assistant-message-meta span,
.assistant-message-meta button { padding: 4px 8px; border: 0; border-radius: 6px; background: #fff; color: #075985; font-size: 11px; font-weight: 800; }
.assistant-message-meta button { cursor: pointer; }
.assistant-message-text { margin: 0; white-space: pre-wrap; line-height: 1.75; }
.assistant-message-attachment { margin: 9px 0 0; font-size: 12px; opacity: .82; }
.assistant-message-attachment i { margin-right: 6px; }

.assistant-business-list { display: grid; gap: 10px; margin-top: 12px; }
.assistant-business-card { padding: 12px; border: 1px solid #e7edf3; border-radius: 10px; background: #fff; }
.assistant-business-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; }
.assistant-business-grid p { margin: 0; color: #64748b; font-size: 12px; line-height: 1.6; }
.assistant-business-grid strong { color: #334155; }
.assistant-business-grid a { color: #0284c7; font-weight: 800; text-decoration: none; }
.assistant-pay-button { margin-top: 10px; padding: 7px 12px; border: 0; border-radius: 8px; background: #059669; color: #fff; cursor: pointer; font-size: 12px; font-weight: 800; }
.assistant-suggestions { flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.assistant-suggestions button { padding: 7px 12px; border: 1px solid #dce5ed; border-radius: 999px; background: #fff; color: #52647a; cursor: pointer; font-size: 12px; font-weight: 700; }
.assistant-suggestions button:hover { border-color: #7dd3fc; background: #f0f9ff; color: #0369a1; }
.assistant-suggestions button:disabled { cursor: not-allowed; opacity: .5; }
.assistant-loading { display: flex; align-items: center; gap: 5px; color: #64748b; font-weight: 700; }
.assistant-loading span { width: 5px; height: 5px; border-radius: 50%; background: #38bdf8; animation: assistantPulse 1s infinite ease-in-out; }
.assistant-loading span:nth-child(2) { animation-delay: .15s; }
.assistant-loading span:nth-child(3) { margin-right: 5px; animation-delay: .3s; }

.assistant-composer { position: relative; margin-top: 16px; overflow: hidden; border: 1px solid #eef2f6; border-radius: 16px; background: #fff; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04); transition: border-color .18s ease, box-shadow .18s ease; }
.assistant-composer.is-dragging { border-color: #38bdf8; box-shadow: 0 0 0 3px rgba(56, 189, 248, .16), 0 8px 24px rgba(15, 23, 42, .06); }
.assistant-drop-overlay { position: absolute; z-index: 5; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; border: 2px dashed #38bdf8; border-radius: 15px; background: rgba(240, 249, 255, .96); color: #0369a1; pointer-events: none; }
.assistant-drop-overlay i { margin-bottom: 2px; font-size: 24px; }
.assistant-drop-overlay strong { font-size: 14px; }
.assistant-drop-overlay span { color: #64748b; font-size: 11px; }
.assistant-input-row { display: flex; align-items: flex-end; gap: 14px; padding: 13px 14px; }
.assistant-input-row textarea { min-height: 72px; flex: 1; resize: none; border: 0; border-radius: 10px; outline: 0; background: #f7f9fc; padding: 12px 14px; color: #334155; font: inherit; font-size: 14px; line-height: 1.6; }
.assistant-input-row textarea:focus { box-shadow: inset 0 0 0 1px #7dd3fc; background: #fff; }
.assistant-send { display: grid; width: 48px; height: 48px; flex: 0 0 auto; place-items: center; border: 0; border-radius: 50%; background: linear-gradient(135deg, #0077b6, #00b4d8); color: #fff; cursor: pointer; font-size: 17px; }
.assistant-send:disabled { cursor: not-allowed; background: #e2e8f0; color: #94a3b8; }
.assistant-attachment-row { min-height: 47px; justify-content: space-between; gap: 14px; padding: 9px 16px; border-top: 1px solid #eef2f6; }
.assistant-attachment-actions { gap: 16px; }
.assistant-attachment-actions button { border: 0; background: transparent; color: #64748b; cursor: pointer; font-size: 13px; }
.assistant-attachment-actions button:hover { color: #0284c7; }
.assistant-attachment-chip { display: flex; min-width: 0; align-items: center; gap: 7px; padding: 5px 8px; border-radius: 8px; background: #f0f9ff; color: #0369a1; font-size: 11px; font-weight: 700; }
.assistant-attachment-chip span { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.assistant-attachment-chip button { border: 0; background: transparent; color: inherit; cursor: pointer; }
.assistant-no-attachment { color: #94a3b8; font-size: 11px; font-weight: 600; }

.assistant-results { margin-top: 16px; padding: 16px; border: 1px solid #e7edf3; border-radius: 16px; background: #fff; }
.assistant-results-title { justify-content: space-between; gap: 12px; }
.assistant-results-title h3 { margin: 0; color: #10223d; font-size: 17px; }
.assistant-results-title p { margin: 4px 0 0; color: #64748b; font-size: 11px; }
.assistant-results-title > span { padding: 6px 10px; border-radius: 999px; background: #ecfdf5; color: #059669; font-size: 11px; font-weight: 800; }
.assistant-cart-feedback { display: flex; min-height: 52px; align-items: center; gap: 11px; margin-top: 12px; padding: 10px 12px; border: 1px solid; border-radius: 11px; }
.assistant-cart-feedback.is-success { border-color: #86efac; background: #f0fdf4; color: #166534; }
.assistant-cart-feedback.is-warning { border-color: #fed7aa; background: #fff7ed; color: #9a3412; }
.assistant-cart-feedback.is-error { border-color: #fecaca; background: #fff7f7; color: #b91c1c; }
.assistant-cart-feedback-icon { display: grid; width: 28px; height: 28px; flex: 0 0 auto; place-items: center; border-radius: 50%; background: currentColor; }
.assistant-cart-feedback-icon i { color: #fff; font-size: 11px; }
.assistant-cart-feedback-copy { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 2px; }
.assistant-cart-feedback-copy strong { font-size: 13px; line-height: 1.4; }
.assistant-cart-feedback-copy span { color: #64748b; font-size: 11px; line-height: 1.5; }
.assistant-cart-feedback-action { flex: 0 0 auto; padding: 6px 10px; border: 1px solid currentColor; border-radius: 7px; background: rgba(255, 255, 255, .75); color: inherit; cursor: pointer; font-size: 11px; font-weight: 800; }
.assistant-cart-feedback-action:hover { background: #fff; }
.assistant-cart-feedback-close { display: grid; width: 28px; height: 28px; flex: 0 0 auto; place-items: center; border: 0; border-radius: 50%; background: transparent; color: #94a3b8; cursor: pointer; }
.assistant-cart-feedback-close:hover { background: rgba(255, 255, 255, .8); color: #475569; }
.assistant-order-info { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 20px; margin-top: 12px; padding: 12px; border-radius: 10px; background: #eff8ff; color: #334155; font-size: 12px; }
.assistant-order-info p { margin: 0; }
.assistant-order-info span { color: #64748b; }
.assistant-result-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 14px; }
.assistant-result-card { padding: 14px; border: 1px solid; border-radius: 12px; }
.assistant-result-card.is-matched { border-color: #a7f3d0; background: #f0fdf4; }
.assistant-result-card.is-unmatched { border-color: #fecaca; background: #fff7f7; }
.assistant-result-card.is-added { border-color: #34d399; box-shadow: 0 0 0 2px rgba(52, 211, 153, .12); }
.assistant-result-main { display: flex; justify-content: space-between; gap: 12px; }
.assistant-result-card h4 { margin: 8px 0 5px; color: #1e293b; font-size: 14px; }
.assistant-result-card p { margin: 3px 0; color: #64748b; font-size: 11px; }
.assistant-result-badges { display: flex; gap: 6px; }
.assistant-result-badges span { padding: 3px 7px; border-radius: 999px; background: #d1fae5; color: #047857; font-size: 10px; font-weight: 800; }
.assistant-result-badges span.is-danger { background: #dc2626; color: #fff; }
.assistant-result-badges span.is-added { background: #059669; color: #fff; }
.assistant-result-badges span.is-added i { margin-right: 3px; }
.assistant-result-reason { color: #dc2626 !important; }
.assistant-result-price { color: #e11d48; font-size: 16px; white-space: nowrap; }
.assistant-result-actions { justify-content: space-between; gap: 10px; margin-top: 12px; }
.assistant-result-actions button { padding: 8px 12px; border: 0; border-radius: 8px; background: #0077b6; color: #fff; cursor: pointer; font-size: 11px; font-weight: 800; }
.assistant-result-actions button:disabled { cursor: not-allowed; background: #cbd5e1; color: #f8fafc; }

.assistant-footer { min-height: 66px; justify-content: space-between; gap: 16px; padding: 10px 24px; border-top: 1px solid #edf1f5; background: #fff; }
.assistant-footer > span { color: #94a3b8; font-size: 11px; }
.assistant-footer > div { gap: 10px; }
.assistant-footer button { min-width: 88px; padding: 10px 18px; border-radius: 8px; cursor: pointer; font-weight: 800; }
.assistant-cancel-button { border: 1px solid #dce5ed; background: #fff; color: #475569; }
.assistant-apply-button { border: 0; background: linear-gradient(135deg, #0077b6, #00b4d8); color: #fff; }
.assistant-apply-button:disabled { cursor: not-allowed; background: #e2e8f0; color: #94a3b8; }

@keyframes assistantPulse {
  0%, 80%, 100% { opacity: .35; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}

@media (max-height: 800px) and (min-width: 769px) {
  .assistant-shell { padding-top: 12px; padding-bottom: 12px; }
  .assistant-chat-thread { min-height: 230px; }
  .assistant-input-row textarea { min-height: 62px; }
}

@media (max-width: 768px) {
  .assistant-header { min-height: 72px; padding: 10px 12px; }
  .assistant-logo { width: 42px; height: 42px; border-radius: 11px; font-size: 26px; }
  .assistant-brand h2 { font-size: 17px; }
  .assistant-brand p { display: none; }
  .assistant-state { display: none; }
  .assistant-header-button span { display: none; }
  .assistant-header-button { width: 36px; height: 36px; justify-content: center; padding: 0; }
  .assistant-shell { padding: 10px; }
  .assistant-chat-thread { min-height: 280px; max-height: 48vh; padding: 12px; }
  .assistant-message-bubble { max-width: 88%; padding: 11px 12px; }
  .assistant-business-grid,
  .assistant-order-info,
  .assistant-result-grid { grid-template-columns: 1fr; }
  .assistant-cart-feedback { align-items: flex-start; flex-wrap: wrap; }
  .assistant-cart-feedback-copy { flex-basis: calc(100% - 80px); }
  .assistant-cart-feedback-action { margin-left: 39px; }
  .assistant-attachment-chip span { max-width: 130px; }
  .assistant-footer { padding: 9px 12px; }
  .assistant-footer > span { display: none; }
  .assistant-footer > div { width: 100%; justify-content: flex-end; }
}
</style>
